<template>
  <div>
    <div class="content-header">
      <div class="container-fluid">
        <div class="row mb-2">
          <div class="col-sm-6">
            <h1 class="m-0">{{ this.$route.name }}</h1>
          </div>
          <div class="col-sm-6">
            <ol class="breadcrumb float-sm-right">
              <li class="breadcrumb-item active"><i v-bind:class="routeIcon"></i> {{ $t(this.$route.name) }}</li>
            </ol>
          </div>
        </div>
      </div>
    </div>

    <section class="content">
      <div class="container-fluid">
        <b-row class="col-lg-12">
          <b-form-group class="col-lg-12 col-md-12 col-sm-12 mb-2">
            <b-form-group label="Section" class="mb-0">
              <b-form-radio-group
                v-model="sectionMode"
                buttons
                :options="[
                  { value: 'countryBlock', text: 'CountryBlock' },
                  { value: 'exceptions', text: 'Exceptions' },
                ]"
              />
            </b-form-group>
          </b-form-group>
        </b-row>
      </div>
    </section>

    <section v-show="sectionMode === 'countryBlock'" class="content">
      <div class="container-fluid">
        <b-row class="col-lg-12">
          <div class="col-lg-6 col-md-6 col-sm-12">
            <b-form-group label-cols-lg="3" label-for="block-code-filter-input" label="Code">
              <CountryCodeSelect input-id="block-code-filter-input" v-model="blockFilters.code" />
            </b-form-group>
          </div>
        </b-row>
        <b-row class="col-lg-12">
          <div class="col-lg-12 col-md-12 col-sm-12 apply-clear">
            <FilterButtons
              :cancel="blockCancel"
              :loadFn="loadBlockedCountries"
              :cancelFn="cancelBlockRequest"
              :resetFn="clearBlockFilters"
            />
          </div>
        </b-row>

        <div class="row col-lg-12">
          <div class="form-group col-sm-3">
            <BlockCountryForm @finish="onBlockCountryFinished" />
          </div>
        </div>

        <div class="row col-12" id="block-loading-container">
          <div class="col-12 mb-10">
            <div class="table-wrapper mb-10">
              <b-table
                :striped="true"
                :small="true"
                :hover="true"
                :empty-text="blockEmptyText"
                show-empty
                :busy="!!blockCancel"
                :items="blockedItems"
                :fields="blockFields"
                primary-key="id"
              >
                <template #cell(actions)="row">
                  <b-button variant="outline-danger" size="xs" @click="openUnblockConfirm(row.item)">
                    <i class="fas fa-unlock"></i> Unblock
                  </b-button>
                </template>
              </b-table>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section v-show="sectionMode === 'exceptions'" class="content">
      <div class="container-fluid">
        <b-row class="col-lg-12">
          <div class="col-lg-6 col-md-6 col-sm-12">
            <b-form-group label-cols-lg="3" label-for="exceptions-code-filter-input" label="Code">
              <CountryCodeSelect input-id="exceptions-code-filter-input" v-model="exceptionFilters.code" />
            </b-form-group>
          </div>
          <div class="col-lg-6 col-md-6 col-sm-12">
            <b-form-group label-cols-lg="3" label-for="exceptions-operator-filter-input" label="Operator">
              <div class="d-flex align-items-start">
                <multiselect
                  id="exceptions-operator-filter-input"
                  v-model="exceptionFilters.operator"
                  :options="operatorOptions"
                  label="label"
                  track-by="value"
                  :loading="!operatorsLoaded"
                  :multiple="true"
                  placeholder="Select operators"
                  class="flex-grow-1"
                />
                <b-button
                  v-if="exceptionFilters.operator && exceptionFilters.operator.length"
                  variant="light"
                  size="sm"
                  class="ml-1 clear-btn"
                  @click="exceptionFilters.operator = []"
                >
                  <i class="fas fa-times"></i>
                </b-button>
              </div>
            </b-form-group>
          </div>
        </b-row>
        <b-row class="col-lg-12">
          <div class="col-lg-12 col-md-12 col-sm-12 apply-clear">
            <FilterButtons
              :cancel="exceptionCancel"
              :loadFn="loadExceptionItems"
              :cancelFn="cancelExceptionRequest"
              :resetFn="clearExceptionFilters"
            />
          </div>
        </b-row>

        <div class="row col-lg-12">
          <div class="form-group col-sm-12 d-flex flex-wrap">
            <ExceptionsManageForm class="mr-2 mb-2" @finish="loadExceptionItems" />
            <ExceptionsCopyForm class="mb-2" @finish="loadExceptionItems" />
          </div>
        </div>

        <div class="row col-12" id="exceptions-loading-container">
          <div class="col-12 mb-10">
            <div class="table-wrapper mb-10">
              <b-table
                :striped="true"
                :small="true"
                :hover="true"
                :empty-text="exceptionEmptyText"
                show-empty
                :busy="!!exceptionCancel"
                :items="exceptionItems"
                :fields="exceptionFields"
                primary-key="rowId"
              >
                <template #cell(operator)="row">
                  {{ row.item.operatorLabel || '—' }}
                </template>
                <template #cell(exceptions)="row">
                  <div v-if="hasExceptions(row.item)" class="exceptions-cell">
                    <div v-if="row.item.exceptionCurrencies.length">
                      <small class="text-muted">Currency:</small> {{ row.item.exceptionCurrencies.join(', ') }}
                    </div>
                    <div v-if="row.item.exceptionGameTypes.length">
                      <small class="text-muted">Game type:</small> {{ row.item.exceptionGameTypes.join(', ') }}
                    </div>
                    <div v-if="row.item.exceptionTables.length">
                      <small class="text-muted">Table:</small> {{ row.item.exceptionTables.join(', ') }}
                    </div>
                  </div>
                  <span v-else class="text-muted">—</span>
                </template>
                <template #cell(actions)="row">
                  <ExceptionsManageForm
                    :initial-code="row.item.countryCode"
                    :initial-operator="row.item.operator"
                    :initial-exceptions="row.item.exceptionsRaw"
                    title="Edit exceptions"
                    @finish="loadExceptionItems"
                  >
                    <template #button="{ onclick }">
                      <b-button variant="primary" size="xs" @click="onclick"><i class="fas fa-edit"></i> Edit</b-button>
                    </template>
                  </ExceptionsManageForm>
                  <b-button
                    variant="outline-danger"
                    size="xs"
                    class="ml-1"
                    @click="openDeleteExceptionsConfirm(row.item)"
                  >
                    <i class="fas fa-trash"></i> Delete
                  </b-button>
                </template>
              </b-table>
            </div>
          </div>
        </div>
      </div>
    </section>

    <b-modal v-model="unblockModalShow" size="lg" title="Confirm unblock?" hide-footer @hidden="resetUnblockModal">
      <span v-if="unblockTarget">
        Please confirm unblocking country <b>{{ unblockTarget.code }}</b> for <b>all operators</b>.
        Saved exceptions will be hidden until the country is blocked again.
      </span>
      <b-button class="mt-2 float-right" variant="danger" :disabled="unblocking" @click="confirmUnblock">
        Unblock
      </b-button>
    </b-modal>

    <b-modal
      v-model="deleteExceptionsModalShow"
      size="lg"
      title="Confirm delete exceptions?"
      hide-footer
      @hidden="resetDeleteExceptionsModal"
    >
      <span v-if="deleteExceptionsTarget">
        Please confirm deleting all exceptions for country <b>{{ deleteExceptionsTarget.countryCode }}</b>
        and operator <b>{{ deleteExceptionsTarget.operatorLabel }}</b>.
      </span>
      <b-button class="mt-2 float-right" variant="danger" :disabled="deletingExceptions" @click="confirmDeleteExceptions">
        Delete
      </b-button>
    </b-modal>
  </div>
</template>

<script>
import axios from "axios";
import { getRouteIcon, ROUTE_PROXY_COUNTRY_USE } from "../../utils/routerIcons";
import FilterButtons from "../../components/FilterButtons.vue";
import Multiselect from "vue-multiselect";
import BlockCountryForm from "../../components/CountryBlocks/BlockCountryForm.vue";
import ExceptionsManageForm from "../../components/CountryBlocks/ExceptionsManageForm.vue";
import ExceptionsCopyForm from "../../components/CountryBlocks/ExceptionsCopyForm.vue";
import CountryCodeSelect from "../../components/CountryBlocks/CountryCodeSelect.vue";
import {
  deleteExceptionItems,
  fetchAllExceptionRows,
  normalizePartnerId,
} from "../../components/CountryBlocks/exceptionUtils";
import { formatGameTypeDisplay, buildGameTypeOptionsForSelect } from "../../components/CountryBlocks/gameTypeUtils";

const getDefaultBlockFilters = () => ({ code: null });
const getDefaultExceptionFilters = () => ({ code: null, operator: [] });

export default {
  components: {
    FilterButtons,
    Multiselect,
    BlockCountryForm,
    ExceptionsManageForm,
    ExceptionsCopyForm,
    CountryCodeSelect,
  },
  data() {
    return {
      sectionMode: 'countryBlock',
      blockCancel: null,
      exceptionCancel: null,
      blockError: null,
      exceptionError: null,
      operatorsLoaded: false,
      operatorOptions: [],
      blockFields: [
        { key: 'code', label: 'Code' },
        { key: 'name', label: 'Name' },
        { key: 'actions', label: 'Actions' },
      ],
      exceptionFields: [
        { key: 'countryCode', label: 'Code' },
        { key: 'operator', label: 'Operator' },
        { key: 'exceptions', label: 'Exceptions' },
        { key: 'actions', label: 'Actions' },
      ],
      blockedItems: [],
      exceptionItems: [],
      blockFilters: getDefaultBlockFilters(),
      exceptionFilters: getDefaultExceptionFilters(),
      unblockModalShow: false,
      unblockTarget: null,
      unblocking: false,
      deleteExceptionsModalShow: false,
      deleteExceptionsTarget: null,
      deletingExceptions: false,
    };
  },
  computed: {
    blockEmptyText() {
      if (this.blockCancel) return 'Loading items...';
      if (this.blockError) return String(this.blockError);
      return 'There are no blocked countries';
    },
    exceptionEmptyText() {
      if (this.exceptionCancel) return 'Loading items...';
      if (this.exceptionError) return String(this.exceptionError);
      return 'There are no exceptions to show';
    },
    routeIcon: () => getRouteIcon(ROUTE_PROXY_COUNTRY_USE),
  },
  watch: {
    sectionMode(mode) {
      if (mode === 'countryBlock') {
        this.loadBlockedCountries();
      } else {
        this.loadExceptionItems();
      }
    },
  },
  methods: {
    clearBlockFilters() {
      this.blockFilters = getDefaultBlockFilters();
      this.loadBlockedCountries();
    },
    clearExceptionFilters() {
      this.exceptionFilters = getDefaultExceptionFilters();
      this.loadExceptionItems();
    },
    openUnblockConfirm(row) {
      this.unblockTarget = row;
      this.unblockModalShow = true;
    },
    resetUnblockModal() {
      this.unblockTarget = null;
      this.unblocking = false;
    },
    openDeleteExceptionsConfirm(row) {
      this.deleteExceptionsTarget = row;
      this.deleteExceptionsModalShow = true;
    },
    resetDeleteExceptionsModal() {
      this.deleteExceptionsTarget = null;
      this.deletingExceptions = false;
    },
    async confirmDeleteExceptions() {
      if (!this.deleteExceptionsTarget) return;

      this.deletingExceptions = true;
      try {
        await deleteExceptionItems(
          (config) => this.$http(config),
          this.deleteExceptionsTarget.exceptionsRaw,
        );
        this.$toastr.i('Exceptions deleted');
        this.deleteExceptionsModalShow = false;
        this.loadExceptionItems();
      } catch (err) {
        const message = err.response?.data?.message ?? err.message;
        this.$toastr.e(typeof message === 'string' ? message : JSON.stringify(message));
      } finally {
        this.deletingExceptions = false;
      }
    },
    onBlockCountryFinished() {
      this.loadBlockedCountries();
      if (this.sectionMode === 'exceptions') {
        this.loadExceptionItems();
      }
    },
    confirmUnblock() {
      if (!this.unblockTarget) return;

      this.unblocking = true;
      this.$http({
        url: '/api/v1/country-blocks/' + this.unblockTarget.id,
        method: 'PUT',
        data: { blocked: false },
        headers: { 'Content-Type': 'application/json' },
      })
        .then(() => {
          this.$toastr.i('Country unblocked');
          this.unblockModalShow = false;
          this.loadBlockedCountries();
          if (this.sectionMode === 'exceptions') {
            this.loadExceptionItems();
          }
        })
        .catch((err) => {
          this.$toastr.e(JSON.stringify(err.response?.data?.message ?? err.message));
        })
        .finally(() => {
          this.unblocking = false;
        });
    },
    loadOperators() {
      return this.$http({ url: '/api/v1/country-partners-for-select', method: 'GET' })
        .then(resp => { this.operatorOptions = resp.data; this.operatorsLoaded = true; })
        .catch(() => { this.$toastr.e('Failed to load operators'); });
    },
    operatorLabelFor(partnerId, operatorMap) {
      if (!partnerId) return 'All operators';
      return operatorMap[partnerId] || partnerId;
    },
    hasExceptions(item) {
      return item.exceptionCurrencies?.length > 0
        || item.exceptionGameTypes?.length > 0
        || item.exceptionTables?.length > 0;
    },
    getTableMap() {
      const tableMap = {};
      this.$store.getters['tablesForSelect/list'].forEach((t) => { tableMap[Number(t.value)] = t.label; });
      return tableMap;
    },
    formatExceptions(cur, gt, tbl) {
      const tableMap = this.getTableMap();
      const gameTypeOptions = buildGameTypeOptionsForSelect(this.$store.getters['gameTypes/list'] || []);
      return {
        exceptionCurrencies: cur.map(i => (i.currency ? i.currency : 'ALL currencies')),
        exceptionGameTypes: gt.map(i => formatGameTypeDisplay(gameTypeOptions, i)),
        exceptionTables: tbl.map(i => {
          const label = tableMap[i.tableId] ?? i.tableId;
          return `${label} (#${i.tableId})`;
        }),
        exceptionsRaw: { currency: cur, gameType: gt, table: tbl },
      };
    },
    mapExceptionRows(rows) {
      const operatorMap = {};
      this.operatorOptions.forEach(o => { operatorMap[o.value] = o.label; });

      return rows.map((row) => {
        const partnerId = normalizePartnerId(row.partnerId);
        const operatorLabel = this.operatorLabelFor(partnerId, operatorMap);
        const formatted = this.formatExceptions(
          row.exceptionsRaw.currency,
          row.exceptionsRaw.gameType,
          row.exceptionsRaw.table,
        );
        return {
          rowId: `${row.countryCode}|${partnerId || ''}`,
          countryCode: row.countryCode,
          partnerId,
          operator: partnerId ? { value: partnerId, label: operatorLabel } : null,
          operatorLabel,
          ...formatted,
        };
      });
    },
    loadBlockedCountries() {
      const loadingInstance = this.$veLoading({
        target: document.querySelector("#block-loading-container"),
        name: "wave",
      });
      this.blockedItems = [];
      this.blockError = null;
      const self = this;
      loadingInstance.show();

      const params = { blocked: 'true' };
      if (this.blockFilters.code && this.blockFilters.code.trim()) {
        params.code = this.blockFilters.code.trim().toUpperCase();
      }

      this.$http({
        url: '/api/v1/country-blocks',
        method: 'GET',
        params,
        cancelToken: new axios.CancelToken(c => { self.blockCancel = c; }),
      })
        .then(resp => { this.blockedItems = resp.data; })
        .catch((err) => { this.blockedItems = []; this.blockError = err; })
        .finally(() => { loadingInstance.close(); this.blockCancel = null; });
    },
    cancelBlockRequest() {
      if (this.blockCancel) this.blockCancel('cancel block request');
    },
    async loadExceptionItems() {
      const loadingInstance = this.$veLoading({
        target: document.querySelector("#exceptions-loading-container"),
        name: "wave",
      });
      this.exceptionItems = [];
      this.exceptionError = null;
      const self = this;
      loadingInstance.show();

      try {
        if (!this.operatorOptions.length) {
          await this.loadOperators();
        }
        await Promise.all([
          this.$store.dispatch('gameTypes/loadList'),
          this.$store.dispatch('tablesForSelect/loadList'),
          this.$store.dispatch('currenciesForSelect/loadList'),
        ]);

        const filters = {};
        if (this.exceptionFilters.code && this.exceptionFilters.code.trim()) {
          filters.countryCode = this.exceptionFilters.code.trim().toUpperCase();
        }
        if (this.exceptionFilters.operator && this.exceptionFilters.operator.length) {
          filters.partnerIds = this.exceptionFilters.operator.map(o => o.value);
        }

        const rows = await fetchAllExceptionRows((config) => this.$http({
          ...config,
          cancelToken: new axios.CancelToken(c => { self.exceptionCancel = c; }),
        }), filters);

        const blockedResp = await this.$http({
          url: '/api/v1/country-blocks',
          method: 'GET',
          params: { blocked: 'true' },
          cancelToken: new axios.CancelToken(c => { self.exceptionCancel = c; }),
        });
        const blockedCodes = new Set((blockedResp.data || []).map(c => c.code));
        const visibleRows = rows.filter(row => blockedCodes.has(row.countryCode));

        this.exceptionItems = this.mapExceptionRows(visibleRows);
      } catch (err) {
        this.exceptionItems = [];
        this.exceptionError = err;
      } finally {
        loadingInstance.close();
        this.exceptionCancel = null;
      }
    },
    cancelExceptionRequest() {
      if (this.exceptionCancel) this.exceptionCancel('cancel exception request');
    },
  },
  mounted() {
    this.loadOperators();
    this.loadBlockedCountries();
  },
};
</script>

<style scoped>
.exceptions-cell > div + div {
  margin-top: 2px;
}
.clear-btn {
  margin-top: 2px;
}
</style>
